import React from "react";
import { Container, Row, Col, Card } from "reactstrap";
import { Landmark, Globe2, Cpu, Users } from "lucide-react";
import {
  SA_PRIMARY,
  SA_SECONDARY,
  SA_ACCENT,
  saGradientLine,
  saGradientTitleStyle,
  saDarkCardBase,
  saReadableText,
  saMutedText,
  onDarkCardEnter,
  onDarkCardLeave,
} from "./saSectionTokens";
import { EVENT_LOCATION_SHORT } from "../../constants";

function Highlight({ children, color = SA_ACCENT }) {
  return (
    <span style={{ color, fontWeight: 700 }}>
      {children}
    </span>
  );
}

const highlightCards = [
  {
    icon: Landmark,
    title: "Intelligent cities & infrastructure",
    text: "Smart cities, mega projects, transportation, utilities, and urban planning on one stage.",
    color: SA_PRIMARY,
  },
  {
    icon: Globe2,
    title: "Kingdom's premier platform",
    text: "The 5th annual edition — returning to Riyadh as the region's leading digital twin conference.",
    color: SA_SECONDARY,
  },
  {
    icon: Cpu,
    title: "Digital twin & AI depth",
    text: "Predictive operations, intelligent decision-making, sustainability, and lifecycle asset management.",
    color: SA_PRIMARY,
  },
  {
    icon: Users,
    title: "Who meets here",
    text: "Government ministries, giga projects, municipalities, developers, utilities, and technology leaders.",
    color: SA_SECONDARY,
  },
];

function SAEventOverview() {
  const size =
    document.body.clientWidth >= 928 || document.documentElement.clientWidth >= 928;

  const paragraphStyle = {
    fontSize: size ? "1.08rem" : "0.98rem",
    lineHeight: 1.8,
    ...saReadableText,
  };

  return (
    <div className="section py-5" style={{ background: "transparent", position: "relative", zIndex: 1 }}>
      <Container>
        <Row className="justify-content-center mb-4">
          <Col md={11} lg={10} className="text-center">
            <p
              className="mb-3"
              style={{
                fontSize: size ? "1.25rem" : "1.05rem",
                fontWeight: 600,
                color: SA_ACCENT,
                ...saReadableText,
              }}
            >
              {EVENT_LOCATION_SHORT}
            </p>
            <p
              className="mb-0"
              style={{
                fontSize: size ? "1.2rem" : "1.05rem",
                lineHeight: 1.65,
                fontWeight: 600,
                ...saMutedText,
              }}
            >
              Powering Saudi Arabia&apos;s Next Generation of Intelligent Cities, Infrastructure
              &amp; Mega Projects
            </p>
          </Col>
        </Row>

        <Row className="justify-content-center mb-4 mb-lg-5">
          <Col className="text-center" md={12}>
            <h2
              className="text-center mb-3"
              style={{
                fontSize: size ? "2.75rem" : "1.85rem",
                fontWeight: 700,
                letterSpacing: "0.5px",
                ...saGradientTitleStyle,
              }}
            >
              The Kingdom&apos;s Premier Digital Twin Conference
            </h2>
            <div
              style={{
                width: "120px",
                height: "4px",
                borderRadius: "2px",
                margin: "0 auto",
                ...saGradientLine,
              }}
            />
          </Col>
        </Row>

        <Row className="justify-content-center mb-4 mb-lg-5">
          <Col lg={10} className="d-flex">
            <Card
              style={{
                ...saDarkCardBase,
                borderRadius: "12px",
                padding: size ? "2rem" : "1.5rem",
                height: "100%",
                width: "100%",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => onDarkCardEnter(e, SA_PRIMARY)}
              onMouseLeave={onDarkCardLeave}
            >
              <p className="mb-4" style={paragraphStyle}>
                As Saudi Arabia continues to redefine the future of urban development through
                ambitious <Highlight>giga projects</Highlight> and{" "}
                <Highlight>smart city initiatives</Highlight>, the{" "}
                <Highlight color={SA_SECONDARY}>5th Annual KINGDOM DIGITAL TWIN 2026</Highlight> will
                showcase how <Highlight>AI</Highlight> and <Highlight>Digital Twins</Highlight> are
                becoming the intelligence behind this transformation.
              </p>
              <p className="mb-4" style={paragraphStyle}>
                With <Highlight color={SA_SECONDARY}>Riyadh Expo 2030</Highlight>, the{" "}
                <Highlight color={SA_SECONDARY}>2034 FIFA World Cup</Highlight>,{" "}
                <Highlight>Qiddiya</Highlight>, <Highlight>Diriyah Gate</Highlight>,{" "}
                <Highlight>Red Sea Global</Highlight>, <Highlight>ROSHN</Highlight>,{" "}
                <Highlight>Rua Al Madinah</Highlight> to the{" "}
                <Highlight>smart city programmes</Highlight> led by Riyadh&apos;s public authorities,
                the Kingdom is creating <Highlight>connected, data-driven ecosystems</Highlight> that
                are transforming how cities are planned, designed, constructed, operated and
                experienced.
              </p>
              <p className="mb-4" style={paragraphStyle}>
                Delivering projects of this scale requires{" "}
                <Highlight>seamless collaboration</Highlight> between governments, developers,
                engineering consultants and technology providers.{" "}
                <Highlight color={SA_SECONDARY}>AI-powered Digital Twins</Highlight> are enabling{" "}
                <Highlight>real-time data integration</Highlight>,{" "}
                <Highlight>predictive insights</Highlight> and smarter decision-making across the{" "}
                <Highlight>entire asset lifecycle</Highlight>.
              </p>
              <p className="mb-0" style={paragraphStyle}>
                Building on the success of our{" "}
                <Highlight color={SA_SECONDARY}>DIGITAL TWIN conferences</Highlight> across Brisbane,
                Wellington, Dubai, Riyadh, London, Nottingham, Cairo, Auckland and Denver since
                2022, Saifee Events is proud to present the{" "}
                <Highlight color={SA_SECONDARY}>5th Annual KINGDOM DIGITAL TWIN 2026</Highlight> which
                has proved to be our <Highlight>flagship conference</Highlight>. Join industry
                leaders as they explore how <Highlight>AI</Highlight> and{" "}
                <Highlight>Digital Twins</Highlight> are reshaping the future of Saudi Arabia&apos;s
                built environment and accelerating the Kingdom&apos;s{" "}
                <Highlight color={SA_SECONDARY}>Vision 2030</Highlight> ambitions.
              </p>
            </Card>
          </Col>
        </Row>

        <Row className="mb-4 mb-lg-5">
          {highlightCards.map((item, i) => {
            const Icon = item.icon;
            return (
              <Col xs={12} sm={6} lg={3} key={i} className="mb-3 mb-lg-0">
                <Card
                  style={{
                    ...saDarkCardBase,
                    borderRadius: "16px",
                    padding: size ? "1.35rem" : "1.1rem",
                    height: "100%",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => onDarkCardEnter(e, item.color)}
                  onMouseLeave={onDarkCardLeave}
                >
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: "14px",
                      background: `linear-gradient(135deg, ${item.color}33, ${item.color}14)`,
                      border: `2px solid ${item.color}55`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "0.85rem",
                    }}
                  >
                    <Icon size={24} strokeWidth={2.5} style={{ color: item.color }} />
                  </div>
                  <h3
                    className="mb-2"
                    style={{ fontSize: "0.92rem", fontWeight: 700, ...saReadableText }}
                  >
                    {item.title}
                  </h3>
                  <p className="mb-0" style={{ fontSize: "0.84rem", lineHeight: 1.55, ...saMutedText }}>
                    {item.text}
                  </p>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Container>
    </div>
  );
}

export default SAEventOverview;
